import axios, { AxiosError, AxiosInstance } from "axios";
import { getSession } from "@auth0/nextjs-auth0";
import { AxiosResponse } from "axios";

import { WEBSOCKET_CONNECTION_TYPES } from "lib/websockets/constants";
import { getAuthToken } from "lib/auth/token";
import {
  ERRORS,
  GLOBALLY_HANDLED_ERRORS,
  SOCKET_ERRORS,
} from "@/constants/errors";
import { DefaultResponseI } from "shared/lib/interfaces/api";

const applyInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => {
      console.log(`${response.config.baseURL}/${response.config.url}`);
      console.log(response.data);

      return response;
    },
    (e) => {
      const response = e?.response;
      const errorCodeFromResponse = response?.data?.error_code as number;

      // Toast suppressed: interceptor never actually logs the user out
      // (the redirect was disabled long ago), so a noisy "session expired"
      // toast on every unhandled 401 from glensocket was pure UX noise.
      // The 401 still surfaces through the rejected promise to react-query
      // and to the dd.error path, so observability is unchanged.
      if (
        errorCodeFromResponse &&
        !Object.hasOwn(ERRORS, errorCodeFromResponse) &&
        !Object.hasOwn(SOCKET_ERRORS, errorCodeFromResponse) &&
        !Object.hasOwn(GLOBALLY_HANDLED_ERRORS, errorCodeFromResponse) &&
        response?.status === 401
      ) {
        console.warn(
          "[glen-socket] unhandled 401 from socket API",
          response?.config?.url,
          errorCodeFromResponse
        );
      }

      console.log(response?.statusText);

      console.log(`${e?.config.baseURL}${e?.config.url}`);
      console.log(`method:${e?.config.method}`);
      console.log(`responseType:${e?.config.responseType}`);

      console.log(e?.config.headers);

      console.log(e?.data);
      console.log(e?.response?.data);

      return e;
    }
  );
};

export interface TwilioAccessTokenResponseI extends DefaultResponseI {
  token: string;
  identity: string;
}

export interface StartConferenceWaitRoomResponseI extends DefaultResponseI {
  call_id: string;
}

export interface CallerProfileI {
  auto_dial: boolean;
}

export interface CallerProfileResponseI extends DefaultResponseI {
  profile: CallerProfileI;
}

export interface StartCallResponseI extends DefaultResponseI {
  call_id: string;
  to_phone_number: string;
}

export enum WSMessageType {
  SUBSCRIBE = "subscribe",
  AUTH = "auth",
  CALL_SETUP = "call_setup",
  END_SESSION = "end_session",
}

export enum WSMessagesSubscribeIdType {
  USER = "user",
}

export interface WSSubscribeMsg {
  user_id: string;
  id_type: WSMessagesSubscribeIdType;
}

export interface WSCallSetupMsg {
  campaign_id: string;
}

export interface WSAuthTokenMsg {
  auth_token: string;
}

export interface WSMessageI {
  msg_type: WSMessageType;
  msg_subscribe?: WSSubscribeMsg;
  msg_call_setup?: WSCallSetupMsg;
  msg_auth_token?: WSAuthTokenMsg;
}

export interface StartDirectCallParamsI {
  contact_id?: string;
  item_id?: string;
  phone?: string;
}

export interface APII {
  Instance: AxiosInstance;

  getWebSocketConnection(
    errorCallback: (error: Event) => any,
    closeCallback: (close: CloseEvent) => any,
    openCallback: (ws: WebSocket) => any,
    msgCallback: (msg: MessageEvent) => any
  ): WebSocket | null;

  /**
   * Used for getting twilio identity access token
   * for Auto Dilaer, Targeted Dialer, Catchup Dialer, Direct Calls
   */
  getTwilioAccessToken(): Promise<
    AxiosResponse<TwilioAccessTokenResponseI, AxiosError>
  >;
  /**
   * Used for getting twilio identity access token
   * for Glibal Dialer (List Dialer)
   */
  getTwilioAccessTokenV3(): Promise<
    AxiosResponse<TwilioAccessTokenResponseI, AxiosError>
  >;
  startConferenceWaitRoom(params: {
    identity_id: string;
  }): Promise<AxiosResponse<StartConferenceWaitRoomResponseI, AxiosError>>;
  endConferenceWaitRoom(params: {
    conference_call_id: string;
  }): Promise<AxiosResponse<DefaultResponseI>>;
  nudgeServerToInitiateCalls(params: {
    conference_call_id: string;
    list_id?: string;
  }): Promise<AxiosResponse<DefaultResponseI>>;
  startCalls(campaignId: string): Promise<AxiosResponse<DefaultResponseI>>;

  setAutoDial(autodial: boolean): Promise<AxiosResponse<DefaultResponseI>>;
  getCallerProfile(): Promise<AxiosResponse<CallerProfileResponseI>>;
  dequeueCaller(campaignId: string): Promise<AxiosResponse<DefaultResponseI>>;
  startDirectCall(
    campaignId: string,
    params: StartDirectCallParamsI
  ): Promise<AxiosResponse<StartCallResponseI>>;
  startLiveTransfer(
    campaignId: string,
    params: {
      call_id: string;
    }
  ): Promise<AxiosResponse<DefaultResponseI>>;
}

const requests = (API: AxiosInstance): APII => ({
  Instance: API,
  getWebSocketConnection(
    errorCallback: (error: Event) => any,
    closeCallback: (close: CloseEvent) => any,
    openCallback: (ws: WebSocket) => any,
    msgCallback: (msg: MessageEvent) => any
  ) {
    if (process.env.NEXT_PUBLIC_SOCKET_DOMAIN) {
      const ws = new WebSocket(WEBSOCKET_CONNECTION_TYPES.DIALER);

      ws.onclose = closeCallback;
      ws.onopen = async () => {
        AuthenticateWS(ws);
        await openCallback(ws);
      };
      ws.onerror = errorCallback;
      ws.onmessage = msgCallback;

      return ws;
    }
    return null;
  },
  /**
   * Used for getting twilio identity access token
   * for Auto Dilaer, Targeted Dialer, Catchup Dialer, Direct Calls
   */
  getTwilioAccessToken: () => API.get(`/v2/calling/token`),
  /**
   * Used for getting twilio identity access token
   * for Glibal Dialer (List Dialer)
   */
  getTwilioAccessTokenV3: () => API.get(`/v3/calling/token`),
  startConferenceWaitRoom: (params: { identity_id: string }) =>
    API.post(`/v1/calling/list_dialer/create_conference`, params),
  endConferenceWaitRoom: (params: { conference_call_id: string }) =>
    API.post(`/v1/calling/list_dialer/end_conference`, params),
  nudgeServerToInitiateCalls: (params: {
    conference_call_id: string;
    list_id?: string;
  }) =>
    API.post(`/v1/calling/list_dialer/nudge_server_to_initiate_calls`, params),
  startCalls: (campaignId) =>
    API.post(`/v1/calling/campaign/${campaignId}/start`),
  startDirectCall: (campaignId, params) => {
    return API.post(`/v1/calling/campaign/${campaignId}/call`, params);
  },
  startLiveTransfer: (campaignId, params) => {
    return API.post(`v1/calling/campaign/${campaignId}/live_transfer`, params);
  },

  setAutoDial: (autodial) =>
    API.post(`/v1/calling/autodial`, {
      auto_dial: autodial,
    }),
  getCallerProfile: () => API.get(`v1/calling/profile`),
  dequeueCaller: (campaignId) =>
    API.post(`/v1/calling/campaign/${campaignId}/dequeue`),
});

export const glenSocketServerAPI = ({
  token,
  req,
  res,
}: {
  token?: string;
  req?: any;
  res?: any;
}) => {
  let auth0Token;

  if (token) {
    auth0Token = token;
  } else if (req && res) {
    const auth0Session = getSession(req, res);

    auth0Token = auth0Session?.accessToken;
  }

  const API = axios.create({
    baseURL: process.env.NEXT_PUBLIC_SOCKET_API_DOMAIN,
    maxRedirects: 0,
    timeout: process.env.NEXT_PUBLIC_ENV === "production" ? 10000 : 30000,
    responseType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      "accept-encoding": "*",
      Authorization: `Bearer ${auth0Token}`,
    },
  });

  applyInterceptors(API);

  return requests(API);
};

export const glenSocketClientAPI = () => {
  const authToken = getAuthToken();

  const API = axios.create({
    baseURL: process.env.NEXT_PUBLIC_SOCKET_API_DOMAIN,
    maxRedirects: 0,
    timeout: process.env.NEXT_PUBLIC_ENV === "production" ? 10000 : 30000,
    responseType: "json",
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  });

  applyInterceptors(API);

  return requests(API);
};

export const SendWSMessage = (message: WSMessageI, ws: WebSocket) => {
  ws.send(JSON.stringify(message));
};

export const CallingService = (ws: WebSocket) => ({
  authenticate: () => {
    const authToken = getAuthToken();

    SendWSMessage(
      {
        msg_type: WSMessageType.AUTH,
        msg_auth_token: {
          auth_token: authToken,
        },
      },
      ws
    );
  },
  setup: (campaignId?: string) => {
    SendWSMessage(
      {
        msg_type: WSMessageType.CALL_SETUP,
        msg_call_setup: {
          campaign_id: campaignId ?? "",
        },
      },
      ws
    );
  },
  endSession: () => {
    SendWSMessage(
      {
        msg_type: WSMessageType.END_SESSION,
      },
      ws
    );
  },
});

export const AuthenticateWS = (ws: WebSocket) => {
  CallingService(ws).authenticate();
};
